import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { GlobalEventModalContent } from "features/CenterContent/RoleContent/GlobalEvents/GlobalEventModalContent";
import { GlobalEventTempFixText } from "features/CenterContent/RoleContent/GlobalEvents/GlobalEventTempFixText";
import { GlobalEventWarningText } from "features/CenterContent/RoleContent/GlobalEvents/GlobalEventWarningText";
import { RelatedIncident } from "features/CenterContent/RoleContent/GlobalEvents/RelatedIncident";
import {
  GlobalFormSchema,
  globalFormSchema,
} from "features/CenterContent/RoleContent/GlobalEvents/types/formSchema";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import {
  formToGlobalInformationRequest,
  responseToGlobalInformationForm,
} from "features/CenterContent/RoleContent/GlobalEvents/types/transformers";
import { usePostGlobalInfo } from "features/CenterContent/RoleContent/GlobalEvents/usePostGlobalInfo";
import {
  handleInternalMessageSubmit,
  useFormInternalMessage,
} from "features/CenterContent/RoleContent/InternalMessage/utils/useForm";
import { WithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { InfrastructureEventCreatedResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { ActionModal } from "shared/components/ActionModal";
import { Text } from "shared/components/Spor";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";

type GlobalEventModalProps = {
  onClose: () => void;
  event?: WithInternalMessages<GlobalEventResponse>;
  relatedInfrastructureEvent?: InfrastructureEventCreatedResponse;
};

export const GLOBAL_EVENT_QUERY_KEY = "globalEvent";

export const GlobalEventModal: FC<GlobalEventModalProps> = ({
  onClose,
  event,
  relatedInfrastructureEvent,
}) => {
  const countryCodeForDropsRole = useCountryCodeForDropsRole();
  const isEditMode = !!event;
  const isRelatedToIncident =
    !!relatedInfrastructureEvent ||
    (isEditMode && event?.referencedIncidentIds.length > 0);
  const incidentId =
    isEditMode && event?.referencedIncidentIds.length > 0
      ? event.referencedIncidentIds[0]
      : relatedInfrastructureEvent?.incidentId;

  const internalMessagesFormMethods = useFormInternalMessage(
    event?.internalMessages,
  );
  const onInternalMessagesSubmit = handleInternalMessageSubmit(
    internalMessagesFormMethods,
  );

  const formMethods = useFormVerbose<GlobalFormSchema>({
    resolver: zodResolver(globalFormSchema),
    defaultValues: responseToGlobalInformationForm(
      event,
      relatedInfrastructureEvent,
    ),
    mode: "onBlur",
  });

  const { mutateAsync: postGlobalInfo, status: postGlobalInfoStatus } =
    usePostGlobalInfo(
      event?.countryCode ?? countryCodeForDropsRole,
      event?.uuid,
    );
  return (
    <FormProvider {...formMethods}>
      <ActionModal
        title={`${isEditMode ? "Endre" : "Opprett"} global driftsmelding`}
        actionTitle={`${isEditMode ? "Endre" : "Opprett"} hendelse`}
        titleBadge={isRelatedToIncident ? "Infrastrukturavhengig" : "Uavhengig"}
        onClose={onClose}
        onSubmit={formMethods.handleSubmit((data) =>
          postGlobalInfo(
            formToGlobalInformationRequest(data.globalForm, incidentId),
          ).then(onInternalMessagesSubmit),
        )}
        loading={postGlobalInfoStatus === "pending"}
        isSuccess={postGlobalInfoStatus === "success"}
        isError={postGlobalInfoStatus === "error"}
        successMessage={isEditMode ? "Hendelse endret" : "Hendelse opprettet"}
        failureMessage={`Kunne ikke ${isEditMode ? "endre" : "opprette"} hendelsen. Prøv på nytt, eller kontakt IT hvis feilen vedvarer.`}
        internalMessagesFormMethods={internalMessagesFormMethods}
      >
        {isEditMode && event.referencedIncidentIds.length > 0 && (
          <RelatedIncident incidentId={event.referencedIncidentIds[0]} />
        )}
        {isEditMode && (
          <Text variant="xs" width="100%" paddingY="12px">
            <span style={{ fontWeight: "bold" }}>Sist oppdatert:</span>{" "}
            {format(event.updatedAt.toDate(), "dd.MM.yyyy 'kl.' HH:mm")}
          </Text>
        )}
        <GlobalEventModalContent formMethods={formMethods} />
        <GlobalEventTempFixText />
        <GlobalEventWarningText />
      </ActionModal>
    </FormProvider>
  );
};
